import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { simulateSpring, SpringParams } from './spring';

type SpringTrajectoryGraphProps = {
  from: number;
  to: number;
  params: SpringParams;
  durationSeconds?: number;
  width?: number;
  height?: number;
};

/**
 * Static SVG plot of the spring trajectory between `from` and `to`
 * given the supplied {@link SpringParams}. Re-runs simulation on prop change.
 */
export const SpringTrajectoryGraph = ({
  from,
  to,
  params,
  durationSeconds = 4,
  width = 360,
  height = 160,
}: SpringTrajectoryGraphProps) => {
  const { classes } = useStyles();

  const { path, samples, valueMin, valueMax } = useMemo(() => {
    const samples = simulateSpring({ from, to, params, durationSeconds });
    const values = samples.map((s) => s.value);
    const valueMin = Math.min(from, to, ...values);
    const valueMax = Math.max(from, to, ...values);
    const range = valueMax - valueMin || 1;
    const lastT = samples[samples.length - 1]?.t || 1;

    const path = samples
      .map((s, i) => {
        const x = (s.t / lastT) * width;
        const y = height - ((s.value - valueMin) / range) * height;
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

    return { path, samples, valueMin, valueMax };
  }, [from, to, params, durationSeconds, width, height]);

  const targetY =
    height - ((to - valueMin) / (valueMax - valueMin || 1)) * height;

  return (
    <svg
      className={classes.svg}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
    >
      <line
        className={classes.target}
        x1={0}
        x2={width}
        y1={targetY}
        y2={targetY}
      />
      <path className={classes.trajectory} d={path} />
      <text className={classes.axis} x={4} y={12}>
        {valueMax.toFixed(0)}
      </text>
      <text className={classes.axis} x={4} y={height - 4}>
        {valueMin.toFixed(0)}
      </text>
      <text className={classes.axis} x={width - 60} y={height - 4}>
        {(samples[samples.length - 1]?.t ?? 0).toFixed(2)}s
      </text>
    </svg>
  );
};

const useStyles = makeStyles()(({ palette }) => ({
  svg: {
    border: `1px solid ${palette.divider}`,
    borderRadius: 4,
    backgroundColor: palette.background.paper,
  },
  trajectory: {
    fill: 'none',
    stroke: palette.primary.main,
    strokeWidth: 2,
  },
  target: {
    stroke: palette.divider,
    strokeWidth: 1,
    strokeDasharray: '4 4',
  },
  axis: {
    fill: palette.text.secondary,
    fontSize: 10,
    fontFamily: 'monospace',
  },
}));
